/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { EMAIL, PHONE_NUMBER } from "@utils/expreciones";
import { RootState } from "@store/store";
import CustomContryCode from "@components/CustomContryCode/CustomContryCode";
import CenteredLoader from "@components/CenteredLoader";
import { useGetClientsSuppliersByRfcQuery } from "@api/proveedorClienteApi";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import { useLazyGetReferencesQuery, usePatchReferencesMutation, usePostReferencesMutation } from "@api/referenciasApi";
import { useLazyGetReferenceTypesQuery, useLazyGetTaxSystemsQuery } from "@api/catalogs";
import { usePutApplicationCompletedMutation } from "@api/applications";
import { InactivityHandler } from "@components/InactivityHandler";
import { createEmptyReference, getOrdinalText, getReferenceConfig, getReferenceIndex } from "@helpers/funciones";
import { REFERENCES_CODES, REFERENCE_DEAFUALT_VALUES } from "@helpers/constantes";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError, ReferenceType } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import useReferenciasForm from "@hooks/useReferenciasForm";
import { UUID } from "crypto";
import * as Sentry from "@sentry/react";

const OTRO_PROVEEDOR_NAME = "OTRO";

const ReferenciasProveedoresPfae = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isVisible, isRequired, isConfigLoading } = useReferenciasForm("proveedores");

  const applicationData = useSelector((state: RootState) => state.register.application);
  const client = useSelector((state: RootState) => state.register.personal_data);
  const company = useSelector((state: RootState) => state.register.company);
  const application = useSelector((state: RootState) => state.register.application);

  const [triggerPostReferences] = usePostReferencesMutation();
  const [triggerPatchReferences] = usePatchReferencesMutation();
  const supplierReferences = useGetClientsSuppliersByRfcQuery(String(company?.rfc));
  const [putAapplicationCompleted] = usePutApplicationCompletedMutation();
  const [getReferenceTypes] = useLazyGetReferenceTypesQuery();
  const [getReferences] = useLazyGetReferencesQuery();
  const [getTaxSystems] = useLazyGetTaxSystemsQuery();

  const [isLoading, setIsLoading] = useState(true);
  const [referenceType, setReferenceType] = useState<ReferenceType | null>(null);
  const [minReference, setMinReference] = useState(REFERENCE_DEAFUALT_VALUES.min);
  const [requiredReference, setRequiredReference] = useState(REFERENCE_DEAFUALT_VALUES.required);

  const formik = useFormik({
    initialValues: {
      referencia: Array.from({ length: REFERENCE_DEAFUALT_VALUES.required }, () => createEmptyReference("proveedor")),
    },
    validationSchema: yup.object().shape({
      referencia: yup.array().of(
        yup.object().shape({
          proveedor: yup.string().test("required-if-min", "Campo requerido", function (value, params) {
            return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
          }),
          country_code: yup.string().test("required-if-min", "Campo requerido", function (value, params) {
            if (!isRequired("phone_number", true)) return true;
            return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
          }),
          phone: yup
            .string()
            .test("required-if-min", "Campo requerido", function (value, params) {
              if (!isRequired("phone_number", true)) return true;
              return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
            })
            .matches(PHONE_NUMBER, { message: "Número de teléfono inválido", excludeEmptyString: true })
            .test("unique", "El teléfono ya se encuentra registrado.", function (value, params) {
              if (!value || !isVisible("phone_number")) return true;
              const reference_index = getReferenceIndex(params.path);
              const reference_phone =
                (formik.values.referencia[reference_index].country_code || "") +
                (formik.values.referencia[reference_index].phone || "") +
                (formik.values.referencia[reference_index].phone_extension || "");
              const array_items = formik.values.referencia.map((item) => {
                return (item.country_code || "") + (item.phone || "") + (item.phone_extension || "");
              });

              const num_results: number = array_items.filter((item) => item === reference_phone).length;
              return num_results > 1 ? false : true;
            }),
          contact_name: yup
            .string()
            .test("required-if-min", "Campo requerido", function (value, params) {
              if (!isRequired("contact_name", true)) return true;
              return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
            })
            .test("unique", "El nombre de contacto ya se encuentra registrado.", function (value) {
              if (!value || !isVisible("contact_name")) return true;
              const array_items = formik.values.referencia.map((item) =>
                (item.contact_name || "").replaceAll(" ", "").toLowerCase(),
              );
              const name_to_compare = value.replaceAll(" ", "").toLowerCase();
              const num_results: number = array_items.filter((item) => item === name_to_compare).length;
              return num_results > 1 ? false : true;
            }),
          email: yup
            .string()
            .test("required-if-min", "Campo requerido", function (value, params) {
              if (!isRequired("email", true)) return true;
              return getReferenceIndex(params.path) < minReference ? Boolean(value) : true;
            })
            .matches(EMAIL, {
              message: "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)",
              excludeEmptyString: true,
            })
            .test("unique", "El email ya se encuentra registrado.", function (value) {
              if (!value || !isVisible("email")) return true;
              const array_items = formik.values.referencia.map((item) => item.email || "");
              const num_results: number = array_items.filter((item) => item === value).length;
              return num_results > 1 ? false : true;
            }),
        }),
      ),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!formik.isValid || !referenceType) {
          return;
        }

        setSubmitting(true);

        for (let index = 0; index < values.referencia.length; index++) {
          const reference = values.referencia[index];
          if (isReferenceEmpty(reference)) continue;

          const supplierItem = supplierReferences?.data?.suppliers?.find(
            (supplier) => supplier.rfc === reference.proveedor,
          );

          const referenceData = {
            client_details_id: client.id,
            id_ext: "4444",
            phone_number: reference.phone,
            reference_type_id: referenceType.id,
            code_phone_number: reference.country_code,
            phone_extension: reference.phone_extension,
            supplier_name: supplierItem?.name,
            supplier_rfc: supplierItem?.rfc,
            contact_name: reference.contact_name,
            email: reference.email,
            percentage_share: Number(supplierItem?.share),
          };

          if (reference.id) {
            await triggerPatchReferences({
              referenceId: reference.id as UUID,
              body: referenceData,
            }).unwrap();
          } else {
            await triggerPostReferences(referenceData).unwrap();
          }
        }

        await putAapplicationCompleted({
          id_sol: applicationData.id,
          body: {
            references_status: "COMPLETO",
          },
        }).unwrap();

        navigate("/producto/creditosimple/avance_expediente_digitalPFAE");
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "ReferenciasProveedoresPfae - onSubmit",
            values: formik.values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const getFieldError = (fieldName: string, index: number) => {
    const error: any = formik.errors.referencia?.[index];
    if (error && fieldName in error) {
      return error[fieldName];
    }
    return undefined;
  };

  const isReferenceEmpty = (reference: (typeof formik.values.referencia)[number]) =>
    !reference.proveedor &&
    !(isVisible("contact_name") && reference.contact_name) &&
    !(isVisible("email") && reference.email) &&
    !(isVisible("phone_number") && reference.phone);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        if (!client.id) return;
        const [reference_types, tax_systems] = await Promise.all([
          getReferenceTypes().unwrap(),
          getTaxSystems(application.type).unwrap(),
        ]);

        const references_config = tax_systems?.config_extra?.references_config?.[REFERENCES_CODES.proveedor];
        const { total_items: required, min_items: min } = getReferenceConfig(references_config);

        setRequiredReference(required);
        setMinReference(min);

        const reference_type_proveedor = reference_types.find((item) => item.code === REFERENCES_CODES.proveedor);
        if (!reference_type_proveedor) return;

        const references_result = await getReferences({
          clientDetailsId: client.id,
          referenceTypeId: reference_type_proveedor.id,
        }).unwrap();

        const values = references_result
          .filter((item) => item.reference_type_id === reference_type_proveedor.id)
          .slice(0, required)
          .map((item) => {
            return {
              id: item.id,
              country_code: item.code_phone_number,
              phone: item.phone_number,
              contact_name: item.contact_name,
              proveedor: item.supplier_rfc,
              email: item.email,
              phone_extension: item.phone_extension,
              share: item.share,
            };
          });

        for (let index = values.length; index < required; index++) {
          values.push(createEmptyReference("proveedor"));
        }

        formik.setFieldValue("referencia", values);

        setTimeout(() => {
          formik.validateForm();
        }, 200);

        setReferenceType(reference_type_proveedor);

        window.scrollTo(0, 0);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "ReferenciasProveedoresPfae - useEffect",
            values: formik.values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        {isConfigLoading || isLoading ? (
          <CenteredLoader />
        ) : (
          <Box maxWidth="sm" mx="auto" mt={4} px={2}>
            <Typography
              textAlign={"center"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={700}
              color={theme?.palette?.primary?.dark}
            >
              Referencias
            </Typography>

            <Typography
              textAlign={"center"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.primary?.dark}
              mt={2}
            >
              <strong>2</strong> de 2
            </Typography>

            <Typography
              textAlign={"center"}
              color={theme?.palette?.primary?.main}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={600}
              mt={2}
            >
              Ahora, escribe {requiredReference} referencias que sean tus proveedores, considera que podríamos
              contactarlos. Debes completar al menos {minReference} para continuar.
            </Typography>

            <Container maxWidth="sm" sx={{ mt: 2, mb: 2 }}>
              <form onSubmit={formik.handleSubmit} noValidate>
                {formik.values.referencia.map((_, index) => (
                  <div key={index}>
                    <Box mt={4} mb={2}>
                      <Typography
                        textAlign={"left"}
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        fontWeight={400}
                        color={theme?.palette?.primary?.dark}
                      >
                        {getOrdinalText(index + 1)} referencia
                      </Typography>
                    </Box>
                    <FormControl
                      fullWidth
                      required={index < minReference}
                      sx={{
                        ...customStylesAsterisk,
                        mt: 1,
                      }}
                      error={Boolean(
                        formik.touched.referencia?.[index]?.proveedor && getFieldError("proveedor", index),
                      )}
                    >
                      <InputLabel id={`${index}proveedor`}>Proveedores</InputLabel>
                      <Select
                        labelId="proveedor"
                        name={`referencia[${index}].proveedor`}
                        id={`proveedor${index}`}
                        fullWidth
                        required={index < minReference}
                        label="Proveedores"
                        value={formik.values.referencia?.[index]?.proveedor}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        error={Boolean(
                          formik.touched.referencia?.[index]?.proveedor && getFieldError("proveedor", index),
                        )}
                        disabled={formik.isSubmitting || isLoading}
                      >
                        <MenuItem value="">Selecciona</MenuItem>
                        {supplierReferences.isSuccess &&
                          supplierReferences?.data?.suppliers
                            ?.filter(
                              (supplier) =>
                                supplier.name === OTRO_PROVEEDOR_NAME ||
                                !formik.values.referencia.some(
                                  (ref, refIndex) => refIndex !== index && ref.proveedor === supplier.rfc,
                                ),
                            )
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((supplier) => (
                              <MenuItem key={supplier.rfc} value={supplier.rfc}>
                                {supplier.name.toUpperCase()}
                              </MenuItem>
                            ))}
                      </Select>

                      {formik.touched.referencia?.[index]?.proveedor && getFieldError("proveedor", index) && (
                        <FormHelperText>{getFieldError("proveedor", index)}</FormHelperText>
                      )}
                    </FormControl>

                    {isVisible("contact_name") && (
                      <FormControl fullWidth>
                        <TextField
                          name={`referencia[${index}].contact_name`}
                          id={`contact_name${index}`}
                          label="Nombre completo del contacto"
                          margin="normal"
                          required={index < minReference && isRequired("contact_name", true)}
                          fullWidth
                          autoComplete="off"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={Boolean(
                            formik.touched.referencia?.[index]?.contact_name && getFieldError("contact_name", index),
                          )}
                          helperText={
                            formik.touched.referencia?.[index]?.contact_name && getFieldError("contact_name", index)
                          }
                          value={formik.values.referencia?.[index]?.contact_name}
                          disabled={formik.isSubmitting || isLoading}
                        />
                      </FormControl>
                    )}

                    {isVisible("email") && (
                      <TextField
                        name={`referencia[${index}].email`}
                        id={`email${index}`}
                        label="Correo electrónico"
                        margin="normal"
                        required={index < minReference && isRequired("email", true)}
                        fullWidth
                        autoComplete="off"
                        onChange={(e) => {
                          const value_to_set = (e.target.value || "").toLowerCase();
                          formik.setFieldValue(`referencia[${index}].email`, value_to_set);
                        }}
                        onBlur={formik.handleBlur}
                        error={Boolean(formik.touched.referencia?.[index]?.email && getFieldError("email", index))}
                        helperText={formik.touched.referencia?.[index]?.email && getFieldError("email", index)}
                        value={formik.values.referencia?.[index]?.email}
                        disabled={formik.isSubmitting}
                        inputProps={{ maxLength: 100 }}
                      />
                    )}

                    {isVisible("phone_number") && (
                      <>
                        <Grid container spacing={1} mt={1}>
                          <Grid item xs={4} sm={4} md={4}>
                            <CustomContryCode
                              label="Código"
                              name={`referencia[${index}].country_code`}
                              value={formik.values.referencia?.[index]?.country_code}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={Boolean(
                                formik.touched.referencia?.[index]?.country_code &&
                                getFieldError("country_code", index),
                              )}
                              disabled={formik.isSubmitting || isLoading}
                            />
                          </Grid>

                          <Grid item xs={8} sm={8} md={8}>
                            <TextField
                              name={`referencia[${index}].phone`}
                              id={`phone${index}`}
                              type="text"
                              variant="outlined"
                              label="Teléfono"
                              fullWidth
                              required={index < minReference && isRequired("phone_number", true)}
                              value={formik.values.referencia?.[index]?.phone}
                              onChange={(event) => {
                                formik.setFieldValue(
                                  `referencia[${index}].phone`,
                                  event.target.value.replace(/[^0-9]/g, ""),
                                );
                              }}
                              onBlur={formik.handleBlur}
                              error={Boolean(
                                formik.touched.referencia?.[index]?.phone && getFieldError("phone", index),
                              )}
                              helperText={formik.touched.referencia?.[index]?.phone && getFieldError("phone", index)}
                              inputProps={{
                                maxLength: 10,
                                inputMode: "numeric",
                              }}
                              disabled={formik.isSubmitting || isLoading}
                            />
                          </Grid>
                        </Grid>

                        <TextField
                          name={`referencia[${index}].phone_extension`}
                          id={`phone_extension${index}`}
                          label="Extensión del teléfono(si aplica)"
                          margin="normal"
                          fullWidth
                          autoComplete="off"
                          onChange={(e) => {
                            const value_to_set = (e.target.value || "").replace(/[^0-9]/g, "");
                            formik.setFieldValue(`referencia[${index}].phone_extension`, value_to_set);
                          }}
                          onBlur={formik.handleBlur}
                          error={Boolean(
                            formik.touched.referencia?.[index]?.phone_extension &&
                            getFieldError("phone_extension", index),
                          )}
                          helperText={
                            formik.touched.referencia?.[index]?.phone_extension &&
                            getFieldError("phone_extension", index)
                          }
                          value={formik.values.referencia?.[index]?.phone_extension}
                          disabled={formik.isSubmitting}
                          inputProps={{
                            maxLength: 5,
                            inputMode: "numeric",
                          }}
                        />
                      </>
                    )}
                  </div>
                ))}
                <Grid container spacing={1} my={2}>
                  <Grid item xs={6} sm={6} md={6}>
                    <Button
                      variant="blue_outlined"
                      type="button"
                      fullWidth
                      sx={{ padding: "0px", height: "100%" }}
                      onClick={() => {
                        navigate("/producto/creditosimple/avance_expediente_digitalPFAE");
                      }}
                      disabled={formik.isSubmitting || isLoading}
                    >
                      Regresar al inicio
                    </Button>
                  </Grid>

                  <Grid item xs={6} sm={6} md={6}>
                    <Button
                      variant="default"
                      type="submit"
                      fullWidth
                      disabled={!formik.isValid || formik.isSubmitting || isLoading}
                    >
                      Continuar
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Container>
          </Box>
        )}
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default ReferenciasProveedoresPfae;
